import { Flex } from 'components/shared/flex'
import { usePutTaskSetApi } from 'api/task_set'
import { CheckboxField, DateField, InputField } from 'components/shared/input'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useContext, useEffect } from 'react'
import { Button, Modal, OverlayTrigger, Popover } from 'react-bootstrap'
import { Form, useEffectSkipFirst, useForm } from 'utils/hooks'
import { ApiError } from 'utils/network/api_hooks'
import { TaskSet, TaskSetForm } from 'entities/task_set'
import { InstitutionData } from './material_tasks_page'

export type FormViewProps = {
    form: Form<TaskSetForm>
    apiError?: ApiError
}

const FormView = ({ form, apiError }: FormViewProps) => {
    return (
        <>
            <Flex flexDirection="column">
                <InputField disabled form={form} attr="institutionName" label="病院名" />
                <Flex style={{ width: 200 }}>
                    <DateField disabled form={form} attr="deliveryDate" defaultDate={new Date()} label="発送日" />
                </Flex>
                <Flex>
                    <InputField form={form} attr="slipNumberJp" label="伝票番号(ゆうパック)" />
                    <InputField form={form} attr="slipNumberYm" label="送付状番号(ヤマト)" />
                </Flex>
                <CheckboxField required form={form} attr="isOk" checkboxLabel="梱包内容を確認しました" />
            </Flex>
        </>
    )
}

type ConfirmTaskSetFormProps = {
    form: Form<TaskSetForm>
    institutionData: InstitutionData
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const ConfirmTaskSetForm = (props: ConfirmTaskSetFormProps): JSX.Element => {
    const { form } = props
    useEffect(() => {
        form.update((f) => {
            f.institutionName = props.institutionData.institutionName
            f.taskSetIds = props.institutionData.taskSetIds
        })
    }, [props.institutionData])

    return (
        <Modal show={props.show} onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>タスクの完了</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <FormView form={form} />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <OverlayTrigger
                    trigger="hover"
                    placement="right"
                    overlay={
                        form.getValue('isOk') ? (
                            <></>
                        ) : (
                            <Popover id="popover-hint" style={{ zIndex: 10000 }}>
                                <Popover.Body>「梱包内容を確認しました」にチェックが必要です。</Popover.Body>
                            </Popover>
                        )
                    }
                >
                    <span>
                        <Button disabled={!form.getValue('isOk')} variant="success" onClick={props.onComplete}>
                            完了
                        </Button>
                    </span>
                </OverlayTrigger>
            </Modal.Footer>
        </Modal>
    )
}

type EditTaskSetFormProps = {
    taskSet: TaskSet
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const EditTaskSetForm = (props: EditTaskSetFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePutTaskSetApi()
    const form = useForm<TaskSetForm>(props.taskSet)

    useEffectSkipFirst(() => {
        form.set(props.taskSet)
    }, [props.taskSet])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <Modal show={props.show} onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>TaskSetの編集</Modal.Title>
            </Modal.Header>
            <Modal.Body>{form.object.id && <FormView form={form} apiError={api.apiError} />}</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
